import cn from 'classnames';
import _ from 'lodash';
import { MUIDataTableColumn, MUIDataTableMeta, MUIDataTableOptions } from 'mui-datatables';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { MODAL_TYPES } from 'src/appConfig/modal';
import { Button, IconClickable, Table, View } from 'src/components/common';
import { useComponentWillUnmount } from 'src/hooks';
import {
  deleteJobAsync,
  getJobsAsync,
  setJobModalType,
  setRefreshTable,
  setSelectedJob,
} from 'src/redux/jobcodes/actions';
import { INITIAL_JOB } from 'src/redux/jobcodes/reducer';
import { GetJobsParams, JobModalType, JobModalTypes } from 'src/redux/jobcodes/types';
import modalActions from 'src/redux/modal/actions';
import { IRootState } from 'src/redux/rootReducer';
import { LockupValueByLabel } from 'src/utils';
import { isEmpty } from 'src/validations';
import JobsModal from './JobsModal';
import './styles.scss';

const JobCodesContainer: React.FC<Props> = ({
  loading,
  jobs,
  totalJobs,
  onGetJobs,
  onSetOpenModal,
  onShowConfirmModal,
  onCloseConfirmModal,
  onDeleteJob,
  status,
  isRefresh,
  onSetSelectedJob,
  onSetRefreshTable,
}) => {
  const [refresh, doRefresh] = useState<number>(0);
  useComponentWillUnmount(() => onSetOpenModal(null as JobModalType));

  useEffect(() => {
    if (isRefresh) {
      doRefreshTable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefresh]);

  useComponentWillUnmount(() => onSetOpenModal(null as JobModalType));

  const doRefreshTable = () => {
    doRefresh(prev => prev + 1);
    onSetRefreshTable(false);
  };

  const filterParamsJob = (params: GetJobsParams) => {
    let res = params;
    Object.keys(res).forEach(key => {
      if (isEmpty(res[key])) delete res[key];
      if (key === 'status') {
        res['status'] = LockupValueByLabel(res.status[0] as string, status) as string;
      }
    });
    return res;
  };

  const handleGetEmployees = (params: GetJobsParams) => {
    onGetJobs(filterParamsJob(params));
  };

  const handleCloseModal = () => {
    onSetSelectedJob(INITIAL_JOB);
    onSetOpenModal(false as JobModalType);
  };

  const handleEditRow = meta => {
    const index = meta.rowIndex;
    onSetSelectedJob(jobs[index]);
    onSetOpenModal(JobModalTypes.EDIT as JobModalType);
  };

  const onDeleteRow = meta => {
    const jobName = meta.rowData[0] || '';

    onShowConfirmModal({
      type: MODAL_TYPES.yesNoModal,
      data: {
        title: 'Delete Job',
        message: `Are you sure that you want to delete this job? \n "${jobName}"`,
        onOk: () => handleDeleteRow(meta),
        onCancel: onCloseConfirmModal,
      },
    });
  };

  const handleDeleteRow = meta => {
    const job = jobs[meta.rowIndex];

    onCloseConfirmModal();
    onDeleteJob({ id: job.id || 0 });
  };

  const tableColumns: MUIDataTableColumn[] = [
    { name: 'code', label: 'Number', options: { filter: false, sort: true } },
    { name: 'description', label: 'Description', options: { filter: false, sort: false } },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,

        filterOptions: {
          names: status.map(item => item.label),
        },

        customBodyRender: (value: string) => (
          <span className={cn({ 'active-status': value === 'active' }, { 'inactive-status': value === 'inactive' })}>
            {_.capitalize(value)}
          </span>
        ),
      },
    },
    {
      name: '',
      label: 'Action',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_value: any, meta: MUIDataTableMeta) => (
          <>
            <IconClickable
              className={cn('text-is-18', 'ml-2', 'ctn-job__action')}
              name="ic_edit"
              onClick={() => handleEditRow(meta)}
            />
            <IconClickable
              className={cn('text-is-18', 'ml-2', 'ctn-job__action')}
              name="ic_trash"
              onClick={() => onDeleteRow(meta)}
            />
          </>
        ),
      },
    },
  ];

  const tableOptions: MUIDataTableOptions = {
    count: totalJobs,
    searchPlaceholder: 'Search by code or description',
  };

  return (
    <View className="ctn-job mt-32">
      <View className="ctn-job__header mb-32">
        <View>
          <h3>Manage Job Numbers</h3>
        </View>
        <View>
          <Button
            onClick={() => {
              onSetOpenModal(JobModalTypes.ADD as JobModalType);
            }}>
            ADD NEW JOB NUMBER
          </Button>
        </View>
      </View>

      <Table
        title=""
        data={jobs}
        tableOptions={tableOptions}
        isLoading={loading}
        columns={tableColumns}
        refresh={refresh}
        onAction={handleGetEmployees}
      />
      <JobsModal onClose={handleCloseModal} />
    </View>
  );
};

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const mapStateToProps = (state: IRootState) => ({
  loading: state.jobCodes.isLoading,
  jobs: state.jobCodes.jobs,
  totalJobs: state.jobCodes.totalJobs,
  status: state.content.status,
  openModalType: state.jobCodes.openModalType,
  isRefresh: state.jobCodes.isRefresh,
});

const mapDispatchToProps = {
  onGetJobs: getJobsAsync.request,
  onDeleteJob: deleteJobAsync.request,
  onShowConfirmModal: modalActions.showModal,
  onCloseConfirmModal: modalActions.closeModal,
  onSetOpenModal: setJobModalType,
  onSetSelectedJob: setSelectedJob,
  onSetRefreshTable: setRefreshTable,
};

export default connect(mapStateToProps, mapDispatchToProps)(JobCodesContainer);
